import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import {
  Bell,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  CheckCheck,
} from "lucide-react";

import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable, type ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { FilterBar, type FilterConfig } from "@/components/admin/shared/FilterBar";
import { BulkActionBar } from "@/components/admin/shared/BulkActionBar";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useAdminNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotifications,
  type AdminNotification,
  type NotificationFilters,
} from "@/hooks/admin/useNotifications";

// ---------------------------------------------------------------------------
// Severity icon renderer
// ---------------------------------------------------------------------------

const SEVERITY_ICONS = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle2,
} as const;

const SEVERITY_COLORS = {
  info: "text-blue-500",
  warning: "text-amber-500",
  error: "text-red-500",
  success: "text-emerald-500",
} as const;

function SeverityIcon({ severity }: { severity: AdminNotification["severity"] }) {
  const Icon = SEVERITY_ICONS[severity];
  return <Icon className={`h-4 w-4 ${SEVERITY_COLORS[severity]}`} />;
}

// ---------------------------------------------------------------------------
// Filter configuration
// ---------------------------------------------------------------------------

const FILTER_CONFIG: readonly FilterConfig[] = [
  {
    key: "severity",
    label: "Sévérité",
    type: "select",
    options: [
      { value: "info", label: "Info" },
      { value: "warning", label: "Avertissement" },
      { value: "error", label: "Erreur" },
      { value: "success", label: "Succès" },
    ],
  },
  {
    key: "is_read",
    label: "Statut de lecture",
    type: "select",
    options: [
      { value: "false", label: "Non lu" },
      { value: "true", label: "Lu" },
    ],
  },
  {
    key: "date_from",
    label: "Date début",
    type: "date",
  },
  {
    key: "date_to",
    label: "Date fin",
    type: "date",
  },
] as const;

// ---------------------------------------------------------------------------
// Date formatter
// ---------------------------------------------------------------------------

const dateFormatter = new Intl.DateTimeFormat("fr-SN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Notifications() {
  const { t } = useTranslation("admin");

  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  // Build API filters
  const apiFilters: NotificationFilters = useMemo(() => {
    const filters: NotificationFilters = { page, limit: 20 };
    const severity = filterValues.severity as string | undefined;
    const isRead = filterValues.is_read as string | undefined;
    const dateFrom = filterValues.date_from as string | undefined;
    const dateTo = filterValues.date_to as string | undefined;

    return {
      ...filters,
      ...(severity ? { severity: severity as NotificationFilters["severity"] } : {}),
      ...(isRead !== undefined && isRead !== null ? { is_read: isRead === "true" } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    };
  }, [filterValues, page]);

  // Data fetching
  const { data, isLoading, isError } = useAdminNotifications(apiFilters);
  const notifications = data?.notifications ?? [];
  const pagination = data?.pagination;

  // Mutations
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotifications = useDeleteNotifications();

  const handleMarkAllRead = useCallback(() => {
    markAllAsRead.mutate();
  }, [markAllAsRead]);

  const handleBulkMarkRead = useCallback(() => {
    selectedIds.forEach((id) => markAsRead.mutate(id));
    setSelectedIds([]);
  }, [selectedIds, markAsRead]);

  const handleBulkDelete = useCallback(() => {
    deleteNotifications.mutate(selectedIds);
    setSelectedIds([]);
  }, [selectedIds, deleteNotifications]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Table columns
  const columns: readonly ColumnDef<AdminNotification>[] = useMemo(
    () => [
      {
        id: "severity",
        header: "Type",
        cell: (row) => <SeverityIcon severity={row.severity} />,
        mobileVisible: true,
      },
      {
        id: "title",
        header: "Titre",
        cell: (row) => (
          <span className={row.is_read ? "text-stone-600" : "font-semibold text-stone-900"}>
            {row.title}
          </span>
        ),
        sortable: true,
        mobileVisible: true,
      },
      {
        id: "message",
        header: "Message",
        cell: (row) => (
          <span className="max-w-xs truncate text-stone-500">{row.message}</span>
        ),
        mobileVisible: true,
      },
      {
        id: "created_at",
        header: "Date",
        cell: (row) => (
          <span className="text-xs text-stone-500">
            {dateFormatter.format(new Date(row.created_at))}
          </span>
        ),
        sortable: true,
        mobileVisible: false,
      },
      {
        id: "is_read",
        header: "Lu",
        cell: (row) =>
          row.is_read ? (
            <CheckCheck className="h-4 w-4 text-stone-300" />
          ) : (
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
          ),
        mobileVisible: true,
      },
    ],
    [],
  );

  // Error state
  if (isError) {
    return (
      <AdminLayout>
        <EmptyState
          icon={<AlertCircle className="h-6 w-6" />}
          title="Erreur de chargement"
          description="Impossible de charger les notifications. Veuillez réessayer."
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              Notifications
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              Gérez vos notifications administratives
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={markAllAsRead.isPending}
            className="gap-2 self-start"
          >
            <CheckCheck className="h-4 w-4" />
            Tout marquer comme lu
          </Button>
        </div>

        {/* Filters */}
        <FilterBar
          filters={FILTER_CONFIG}
          values={filterValues}
          onChange={handleFilterChange}
        />

        {/* Data table */}
        <AdminDataTable<AdminNotification>
          columns={columns}
          data={notifications}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          onRowSelect={setSelectedIds}
          isLoading={isLoading}
          emptyMessage="Aucune notification"
        />

        {/* Bulk actions bar */}
        <BulkActionBar
          selectedCount={selectedIds.length}
          actions={[
            {
              label: "Marquer comme lu",
              onClick: handleBulkMarkRead,
            },
            {
              label: "Supprimer",
              onClick: handleBulkDelete,
              variant: "destructive",
            },
          ]}
          onClear={handleClearSelection}
        />
      </div>
    </AdminLayout>
  );
}
